import { useCallback, useMemo } from 'react';
import { difference } from 'underscore';

import { BrandliveEvent, IEventTag } from '@/types/projects.types';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { Session } from '@/types/session.types';
import { saveWorkingProject } from '@/store/slices/project';
import { selectThemePacks } from '@/store/slices/templates/selectors';
import useGetWorkingProjectThemeColorsList from '@/utils/colors/use-get-working-project-theme-colors-list';
import { CLASSIC_COLOR_PACK_ID, EPaletteModes } from '@/types/theme-packs.types';
import TagsSelect from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/tag-select/tags-select';
import useHasPermission from '@/utils/authentication/use-has-permission';
import { GENERAL_EDITABLE_PERMISSIONS } from '@/types/permissions.types';

type EditTagsProps = {
  sessionUuid?: string | null;
};

const EditTags = ({ sessionUuid }: EditTagsProps) => {
  const workingProject = useAppSelector(getWorkingProject);
  const themePacks = useAppSelector(selectThemePacks);

  const dispatch = useAppDispatch();

  const { hasPermission: hasPermissions } = useHasPermission({
    allowedRoles: GENERAL_EDITABLE_PERMISSIONS,
  });

  const session = useMemo(() => {
    if (!sessionUuid) {
      return workingProject?.sessions[0];
    }
    return workingProject?.sessions.find((s) => s.uuid === sessionUuid);
  }, [workingProject?.sessions, sessionUuid]);

  const tags = useMemo(() => {
    if (!session?.tags) return [];
    const sessionTagsMap: Record<string, string> = {};
    session?.tags.forEach((tag) => {
      sessionTagsMap[tag] = tag;
    });
    return workingProject?.tags?.filter((tag) => sessionTagsMap[tag.uuid]) || [];
  }, [session?.tags, workingProject?.tags]);

  const displayTags = useMemo(() => {
    // remove dupes (needed because saving adds the working tags into the project tags)
    // and we briefly have duplicates
    const tagsMap: Record<string, string> = {};
    return tags.filter((tag) => {
      if (tagsMap[tag.uuid]) return false;
      tagsMap[tag.uuid] = tag.uuid;
      return true;
    });
  }, [tags]);

  const themePackId = workingProject?.settings?.design?.colors?.color_pack || CLASSIC_COLOR_PACK_ID;

  const themePack = useMemo(() => {
    if (!themePacks) return null;
    return themePacks.find((themePack) => themePack.color_pack === themePackId);
  }, [themePackId, themePacks]);

  const presetColors = useGetWorkingProjectThemeColorsList();

  const handleCloseAndSave = useCallback(
    (updatedEventTags: IEventTag[], compare?: boolean) => {
      if (!workingProject) return;
      if (!session) return;

      const updatedTagUuids = updatedEventTags.map((t) => t.uuid);
      const displayTagsUuids = displayTags.map((t) => t.uuid);

      // if there's no difference between the display tags and the updated tags, then return
      // note, order matters here, so we can't just compare the arrays
      if (compare && difference(displayTagsUuids, updatedTagUuids).length === 0) {
        return;
      }

      const updatedProject: BrandliveEvent = {
        ...workingProject,
        tags: updatedEventTags,
        sessions: workingProject.sessions.map((s) => {
          if (s.uuid === session.uuid) {
            return {
              ...s,
              tags: [...(s.tags || []), ...updatedTagUuids],
            };
          }
          return s;
        }),
        ...(workingProject?.sessionsPreviews
          ? {
              sessionsPreviews: workingProject.sessionsPreviews.map((s) => {
                if (s.uuid === session.uuid) {
                  return {
                    ...s,
                    tags: [...(s.tags || []), ...updatedTagUuids],
                  };
                }
                return s;
              }),
            }
          : {}),
      };
      const updatedSession: Session = {
        ...session,
        tags: [...(session?.tags || []), ...updatedTagUuids],
      };

      // We need to wait for the saveWorkingProject to finish before saving the session
      // so we pass in the updated session and the reducer middleware will handle updating the session
      // after the project has been saved
      const otherSessions = workingProject?.sessions.map((session) =>
        session.session === updatedSession.session ? updatedSession : session
      );
      dispatch(saveWorkingProject({ project: updatedProject, sessions: otherSessions, saveId: 'edit-tags' }));
    },
    [workingProject, session, displayTags, dispatch]
  );

  return (
    <TagsSelect
      tags={displayTags}
      onMenuClose={(updatedTags, options) => {
        handleCloseAndSave(updatedTags, !options?.forceSave);
      }}
      onChange={(tags) => {
        handleCloseAndSave(tags);
      }}
      disabled={!hasPermissions}
      presetColors={presetColors}
      themeMode={themePack?.theme_mode || EPaletteModes.Light}
    />
  );
};

export default EditTags;
